import { motion } from "framer-motion";
import {
  Settings,
  Wrench,
  Activity,
  ClipboardCheck,
  Droplet,
  Disc3,
  Cog,
  Search,
  ArrowUpRight,
} from "lucide-react";
import { WHATSAPP_URL } from "@/constants/site";

const SERVICES = [
  {
    icon: Settings,
    title: "Câmbio Automático",
    text: "Diagnóstico, reparo e manutenção de transmissões automáticas com tecnologia especializada.",
    featured: true,
  },
  {
    icon: Wrench,
    title: "Mecânica em Geral",
    text: "Soluções completas para motor, sistema elétrico e componentes do seu veículo.",
  },
  {
    icon: Activity,
    title: "Diagnóstico Automotivo",
    text: "Leitura computadorizada e análise técnica precisa para resolver qualquer falha.",
  },
  {
    icon: ClipboardCheck,
    title: "Revisão Preventiva",
    text: "Inspeção completa para evitar problemas e prolongar a vida útil do veículo.",
  },
  {
    icon: Droplet,
    title: "Troca de Óleo",
    text: "Lubrificantes premium e filtros adequados às especificações do fabricante.",
  },
  {
    icon: Disc3,
    title: "Freios e Suspensão",
    text: "Segurança e conforto com peças de qualidade e instalação profissional.",
  },
  {
    icon: Cog,
    title: "Manutenção Corretiva",
    text: "Reparos especializados após diagnóstico técnico para garantir performance.",
  },
  {
    icon: Search,
    title: "Avaliação Técnica",
    text: "Inspeção pré-compra ou check-up completo para sua tranquilidade.",
  },
];

export default function Services() {
  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-700/10 blur-[150px] rounded-full" />
      <div className="absolute inset-0 bg-noise opacity-[0.025] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <p className="text-xs uppercase tracking-[0.35em] text-red-500 font-bold mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-red-500" /> Nossos Serviços
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-[1] tracking-tighter text-white">
              Tudo o que seu
              <br />
              <span className="text-red-600">carro precisa.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-zinc-400 text-base md:text-lg max-w-md font-body leading-relaxed"
          >
            Do diagnóstico ao reparo final — atendemos com padrão premium, peças confiáveis e mão de obra técnica.
          </motion.p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <motion.a
              key={s.title}
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`service-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
              className={`group relative block p-7 border transition-all duration-500 overflow-hidden ${
                s.featured
                  ? "bg-red-600/10 border-red-600/40 hover:border-red-500"
                  : "bg-zinc-900/40 border-zinc-800/80 hover:border-red-600/50 hover:bg-zinc-900/70"
              }`}
            >
              {/* Diagonal accent */}
              <div className="absolute -top-10 -right-10 w-24 h-24 border-t-2 border-r-2 border-red-600/0 group-hover:border-red-600/60 transition-all duration-500 rotate-12" />

              <div className="flex items-start justify-between mb-6">
                <div
                  className={`w-12 h-12 flex items-center justify-center border ${
                    s.featured
                      ? "border-red-500/40 bg-red-600/20"
                      : "border-zinc-700 bg-zinc-900"
                  }`}
                >
                  <s.icon size={22} strokeWidth={1.5} className="text-red-500" />
                </div>
                <ArrowUpRight
                  size={18}
                  strokeWidth={1.5}
                  className="text-zinc-600 group-hover:text-red-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
                />
              </div>

              <h3 className="font-display text-lg font-bold text-white uppercase tracking-tight mb-3 leading-tight">
                {s.title}
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.text}</p>

              {s.featured && (
                <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest text-red-300 font-bold bg-red-600/20 px-2 py-0.5 border border-red-500/30">
                  Especialidade
                </span>
              )}
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
